import superagent from 'superagent';
import { backendUrl } from '@helpers/consts';

/**
 * Tolerance Settings API helper using Serena pattern
 * Provides methods for CRUD operations on category tolerance settings
 */
class ToleranceApi {
  constructor() {
    this.baseUrl = `${backendUrl}`;
    this.authHeader = () => ({
      'Authorization': `Bearer ${localStorage.getItem('authJWT')}`,
      'accept': 'json'
    });
  }

  /**
   * Get all product categories (active only)
   */
  async getCategories() {
    try {
      const response = await superagent
        .get(`${this.baseUrl}/product/productCategory`)
        .query({ active: true })
        .set(this.authHeader());
      
      return response.body || [];
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw new Error('Failed to fetch categories');
    }
  }

  /**
   * Get all tolerance settings
   */
  async getToleranceSettings() {
    try {
      const response = await superagent
        .get(`${this.baseUrl}/category-tolerance-settings`)
        .set(this.authHeader());
      
      // Handle response format { success: true, data: [...] }
      if (response.body && response.body.data) {
        return response.body.data;
      }
      
      return response.body || [];
    } catch (error) {
      console.error('Error fetching tolerance settings:', error);
      throw new Error('Failed to fetch tolerance settings');
    }
  }

  /**
   * Get tolerance settings as key-value map
   */
  async getToleranceSettingsMap() {
    try {
      const response = await superagent
        .get(`${this.baseUrl}/category-tolerance-settings/map/all`)
        .set(this.authHeader());
      
      return response.body || {};
    } catch (error) {
      console.error('Error fetching tolerance settings map:', error);
      throw new Error('Failed to fetch tolerance settings map');
    }
  }

  /**
   * Get tolerance setting by ID
   */
  async getToleranceById(id) {
    try {
      const response = await superagent
        .get(`${this.baseUrl}/category-tolerance-settings/${id}`)
        .set(this.authHeader());
      
      return response.body;
    } catch (error) {
      console.error('Error fetching tolerance setting by ID:', error);
      throw new Error('Failed to fetch tolerance setting');
    }
  }

  /**
   * Get tolerance setting by category name
   */
  async getToleranceByCategory(category) {
    try {
      const response = await superagent
        .get(`${this.baseUrl}/category-tolerance-settings/category/${encodeURIComponent(category)}`)
        .set(this.authHeader());
      
      return response.body;
    } catch (error) {
      console.error('Error fetching tolerance setting by category:', error);
      // Return null if not found (404 is expected for non-existing settings)
      if (error.status === 404) {
        return null;
      }
      throw new Error('Failed to fetch tolerance setting');
    }
  }


  async createTolerance(data) {
    try {
      
      if (!data.category || data.tolerancePercent === undefined || data.toleranceValue === undefined) {
        throw new Error('Category, tolerancePercent, and toleranceValue are required');
      }

      
      if (data.tolerancePercent < 0 || data.tolerancePercent > 100) {
        throw new Error('Tolerance percentage must be between 0 and 100');
      }

      if (data.toleranceValue < 0) {
        throw new Error('Tolerance value must be positive');
      }

      const response = await superagent
        .post(`${this.baseUrl}/category-tolerance-settings`)
        .send({
          category: data.category,
          tolerancePercent: parseFloat(data.tolerancePercent),
          toleranceValue: parseFloat(data.toleranceValue)
        })
        .set(this.authHeader());
      
      
      if (response.body && response.body.data) {
        return response.body.data;
      }
      
      return response.body;
    } catch (error) {
      console.error('Error creating tolerance setting:', error);
      if (error.message.includes('required') || error.message.includes('must be')) {
        throw error;
      }
      throw new Error('Failed to create tolerance setting');
    }
  }


  async updateTolerance(id, data) {
    try {
      // Validate ranges if provided
      if (data.tolerancePercent !== undefined) {
        if (data.tolerancePercent < 0 || data.tolerancePercent > 100) {
          throw new Error('Tolerance percentage must be between 0 and 100');
        }
      }

      if (data.toleranceValue !== undefined) {
        if (data.toleranceValue < 0) {
          throw new Error('Tolerance value must be positive');
        }
      }

      const updateData = {};
      if (data.category !== undefined) updateData.category = data.category;
      if (data.tolerancePercent !== undefined) updateData.tolerancePercent = parseFloat(data.tolerancePercent);
      if (data.toleranceValue !== undefined) updateData.toleranceValue = parseFloat(data.toleranceValue);

      const response = await superagent
        .put(`${this.baseUrl}/category-tolerance-settings/${id}`)
        .send(updateData)
        .set(this.authHeader());
      
      // Handle response format { success: true, data: {...} }
      if (response.body && response.body.data) {
        return response.body.data;
      }
      
      return response.body;
    } catch (error) {
      console.error('Error updating tolerance setting:', error);
      if (error.message.includes('must be')) {
        throw error;
      }
      throw new Error('Failed to update tolerance setting');
    }
  }


  async deleteTolerance(id) {
    try {
      const response = await superagent
        .delete(`${this.baseUrl}/category-tolerance-settings/${id}`)
        .set(this.authHeader());
      
      return response.body;
    } catch (error) {
      console.error('Error deleting tolerance setting:', error);
      throw new Error('Failed to delete tolerance setting');
    }
  }

  
 
  async exportToleranceSettings() {
    try {
      const settings = await this.getToleranceSettings();
      
      // Convert to CSV format
      const headers = ['Category', 'Tolerance Percentage (%)', 'Tolerance Value ($)', 'Created At', 'Updated At'];
      const csvData = [
        headers.join(','),
        ...settings.map(setting => [
          `"${setting.category}"`,
          setting.tolerancePercent,
          setting.toleranceValue,
          `"${new Date(setting.createdAt).toLocaleString()}"`,
          `"${new Date(setting.updatedAt).toLocaleString()}"`
        ].join(','))
      ].join('\n');

      return csvData;
    } catch (error) {
      console.error('Error exporting tolerance settings:', error);
      throw new Error('Failed to export tolerance settings');
    }
  }

  /**
   * Parse CSV data for bulk import
   */
  parseCsvData(csvText) {
    try {
      const lines = csvText.trim().split('\n');
      if (lines.length <= 1) {
        throw new Error('CSV file must contain header and at least one data row');
      }

      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
      const requiredHeaders = ['category', 'tolerance percentage (%)', 'tolerance value ($)'];
      
      // Check if all required headers are present
      const missingHeaders = requiredHeaders.filter(h => 
        !headers.some(header => header.includes(h.split(' ')[0]))
      );
      
      if (missingHeaders.length > 0) {
        throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
      }

      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());
        
        if (values.length >= 3) {
          const categoryIndex = headers.findIndex(h => h.includes('category'));
          const percentIndex = headers.findIndex(h => h.includes('percentage') || h.includes('%'));
          const valueIndex = headers.findIndex(h => h.includes('value') && h.includes('$'));

          const category = values[categoryIndex];
          const tolerancePercent = parseFloat(values[percentIndex]);
          const toleranceValue = parseFloat(values[valueIndex]);

          if (category && !isNaN(tolerancePercent) && !isNaN(toleranceValue)) {
            data.push({
              category,
              tolerancePercent,
              toleranceValue
            });
          }
        }
      }

      return data;
    } catch (error) {
      console.error('Error parsing CSV data:', error);
      throw new Error(`Failed to parse CSV: ${error.message}`);
    }
  }
}

// Export singleton instance
export const toleranceApi = new ToleranceApi();
